import Dexie, { type EntityTable } from 'dexie';
import type { Estimate, MasterData, OutboxItem, User } from '@fruitways/shared';

/**
 * Everything the phone knows, so every screen renders with no signal.
 *
 * - estimates: server rows plus locally captured ones (status `queued`). Keyed by estimate id.
 * - outbox: unsent actions in insertion order. Drained by useOutbox.
 * - kv: single documents (master data snapshot, signed-in user, last refresh time).
 */
export interface KvRow<T = unknown> {
  key: string;
  value: T;
  updatedAt: string;
}

export class HarvestDb extends Dexie {
  estimates!: EntityTable<Estimate, 'id'>;
  outbox!: EntityTable<OutboxItem & { seq?: number }, 'id'>;
  kv!: EntityTable<KvRow, 'key'>;

  constructor() {
    super('fruitways-harvest');
    this.version(1).stores({
      estimates: 'id, farmId, weekStart, status, [farmId+weekStart]',
      outbox: 'id, seq, createdAt, estimateId',
      kv: 'key',
    });
  }
}

export const db = new HarvestDb();

export const KV = {
  masterData: 'masterData',
  user: 'user',
  lastServerSync: 'lastServerSync',
  /** Admin only: the grower list and the variety harvest weeks, for reading with no signal. */
  adminGrowers: 'adminGrowers',
  adminHarvestWeeks: 'adminHarvestWeeks',
  adminUserFarms: 'adminUserFarms',
} as const;

export async function getKv<T>(key: string): Promise<T | undefined> {
  const row = await db.kv.get(key);
  return row?.value as T | undefined;
}

export async function setKv<T>(key: string, value: T): Promise<void> {
  await db.kv.put({ key, value, updatedAt: new Date().toISOString() });
}

export async function getMasterData(): Promise<MasterData | undefined> {
  return getKv<MasterData>(KV.masterData);
}

export async function getStoredUser(): Promise<User | undefined> {
  return getKv<User>(KV.user);
}

/** Replace server-known estimates while keeping anything still queued on this phone. */
export async function replaceServerEstimates(rows: Estimate[]): Promise<void> {
  await db.transaction('rw', db.estimates, async () => {
    const queued = await db.estimates.where('status').equals('queued').toArray();
    const queuedIds = new Set(queued.map((q) => q.id));
    await db.estimates.where('status').notEqual('queued').delete();
    await db.estimates.bulkPut(rows.filter((r) => !queuedIds.has(r.id)));
  });
}

/** Wipe everything on log out. The phone should hold nothing for the next person. */
export async function clearAll(): Promise<void> {
  await db.transaction('rw', db.estimates, db.outbox, db.kv, async () => {
    await Promise.all([db.estimates.clear(), db.outbox.clear(), db.kv.clear()]);
  });
}
